/*
  ReportTranscriptionErrorDialog — spec Phase 1b "Report transcription
  error". Small modal that lets a clinician flag a bad AI transcript
  suggestion. The report is POSTed to
  /api/transcript-suggestions/{id}/feedback, which:

    1. Persists the mistake in the `transcript_suggestion_feedback`
       corpus for later prompt-tuning grep.
    2. Auto-rejects the suggestion so the pill disappears from the
       review pane in one click.

  Reason list is deliberately minimal (spec §11):
    · wrong_value      — the suggested value is wrong.
    · wrong_field      — the value belongs to a different field.
    · hallucinated     — the value never appeared in the transcript.
    · other            — free-text note.
*/
import { useState, useEffect } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select, SelectTrigger, SelectValue,
  SelectContent, SelectItem,
} from "@/components/ui/select";
import { Flag, Mic } from "lucide-react";

const REASONS = [
  { v: "wrong_value",  label: "Wrong value — the answer is incorrect" },
  { v: "wrong_field",  label: "Wrong field — belongs to a different question" },
  { v: "hallucinated", label: "Hallucinated — not in the transcript" },
  { v: "other",        label: "Other (explain below)" },
];

/**
 * Props:
 *   open                 — dialog visibility.
 *   suggestion           — full suggestion row (needs id, field_label,
 *                          proposed_value, evidence.text).
 *   onClose(reported)    — callback; `reported` is true when a report
 *                          was successfully filed (caller refreshes list).
 */
export default function ReportTranscriptionErrorDialog({
  open, suggestion, onClose,
}) {
  const [reason, setReason] = useState("wrong_value");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setReason("wrong_value");
      setNote("");
    }
  }, [open, suggestion?.id]);

  async function submit() {
    if (!suggestion?.id) return;
    if (reason === "other" && !note.trim()) {
      toast.error("Add a note", { description: "Please describe the issue." });
      return;
    }
    setSaving(true);
    try {
      await api.post(`/transcript-suggestions/${suggestion.id}/feedback`, {
        reason,
        correction_note: note,
      });
      toast.success("Reported — thanks for the feedback", {
        description: "The suggestion has been rejected and logged for prompt review.",
      });
      onClose(true);
    } catch (e) {
      toast.error("Could not report", {
        description: e?.response?.data?.detail || e.message,
      });
    } finally { setSaving(false); }
  }

  if (!suggestion) return null;
  const evidenceText = suggestion?.evidence?.text || "";
  const proposedValue = String(suggestion?.proposed_value ?? "");

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v && !saving) onClose(false); }}>
      <DialogContent
        className="max-w-lg p-0 flex flex-col max-h-[85vh]"
        data-testid="report-transcription-error-dialog"
      >
        <DialogHeader className="px-6 pt-6 pb-3 border-b border-zinc-200 shrink-0">
          <DialogTitle className="flex items-center gap-2">
            <Flag className="h-4 w-4 text-rose-600" />
            Report transcription error
          </DialogTitle>
          <div className="text-sm text-zinc-600">
            Field: <b>{suggestion.field_label || suggestion.field_key}</b>
          </div>
        </DialogHeader>

        <div className="px-6 py-4 space-y-3 overflow-y-auto flex-1 min-h-0">
          <div className="rounded border border-violet-300 bg-violet-50 p-3 text-sm">
            <div className="text-2xs uppercase tracking-wider text-violet-900 flex items-center gap-1 font-semibold">
              <Mic className="h-3 w-3" />
              AI-suggested value
            </div>
            <div className="mt-1 whitespace-pre-wrap text-zinc-950">
              {proposedValue || <span className="italic text-zinc-500">— empty —</span>}
            </div>
            {evidenceText && (
              <div className="mt-2 text-xs italic text-zinc-700 border-l-2 border-violet-300 pl-2">
                &quot;{evidenceText}&quot;
              </div>
            )}
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs font-semibold">What's wrong?</Label>
            <Select value={reason} onValueChange={setReason}>
              <SelectTrigger data-testid="report-error-reason">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {REASONS.map((r) => (
                  <SelectItem key={r.v} value={r.v}>{r.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs font-semibold">
              Note {reason === "other" ? "(required)" : "(optional)"}
            </Label>
            <Textarea
              rows={3}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Optional: what would the correct answer have been?"
              data-testid="report-error-note"
            />
            <div className="text-2xs text-zinc-500">
              Reports are stored to a corpus we grep for prompt tuning — no
              patient-identifying content is required.
            </div>
          </div>
        </div>

        <DialogFooter className="px-6 py-3 border-t border-zinc-200 bg-white shrink-0 gap-2">
          <Button
            variant="outline"
            onClick={() => onClose(false)}
            disabled={saving}
            data-testid="report-error-cancel"
          >
            Cancel
          </Button>
          <Button
            className="bg-rose-600 hover:bg-rose-700 text-white"
            onClick={submit}
            disabled={saving}
            data-testid="report-error-submit"
          >
            Report & reject
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
